import React, { useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { NotificationItem } from '../../client/notificationsClient';
import NotificationPanel from './NotificationPanel';

/**
 * The top-bar bell: icon button + unread badge + dropdown panel.
 * Presentational — the connected shell (`ShellPage`) feeds it from
 * `useNotifications` and handles navigation; the design gallery feeds it
 * fixtures. Badge is danger-toned (an unanswered supplier is a "needs you"
 * fact, and violet stays Robbie's); hidden at zero — no fake urgency.
 */
export interface NotificationBellProps {
  unreadCount: number;
  items: NotificationItem[];
  onSelect: (notification: NotificationItem) => void;
  onMarkAllRead: () => void;
  /** Called when the panel opens — the shell refreshes the list here. */
  onOpen?: () => void;
  /** Navy chrome (the wizard header) instead of the light top bar. */
  onDark?: boolean;
  /** Panel open on mount (design gallery / tests only). */
  defaultOpen?: boolean;
  className?: string;
}

const badgeLabel = (count: number): string => (count > 99 ? '99+' : String(count));

export const NotificationBell: React.FC<NotificationBellProps> = ({
  unreadCount,
  items,
  onSelect,
  onMarkAllRead,
  onOpen,
  defaultOpen = false,
  onDark = false,
  className = '',
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onPointerDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  const toggle = () => {
    setOpen((v) => {
      if (!v) onOpen?.();
      return !v;
    });
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        className={`relative flex shrink-0 items-center justify-center rounded-control border ${
          onDark ? 'h-[30px] w-[30px] border-chrome-border hover:bg-white/[0.08]' : 'h-[34px] w-[34px] border-line-strong hover:bg-surface-hover'
        }`}
      >
        <Bell className={`h-4 w-4 ${onDark ? 'text-chrome-text' : 'text-ink-secondary'}`} />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute -right-s1 -top-s1 flex h-[16px] min-w-[16px] items-center justify-center rounded-round border border-surface bg-danger-ink px-[3px] font-mono text-[9.5px] font-bold leading-none text-white"
          >
            {badgeLabel(unreadCount)}
          </span>
        )}
      </button>
      {open && (
        <div role="dialog" aria-label="Notifications" className="absolute right-0 top-full z-40 mt-s2">
          <NotificationPanel
            items={items}
            unreadCount={unreadCount}
            onSelect={(notification) => {
              setOpen(false);
              onSelect(notification);
            }}
            onMarkAllRead={onMarkAllRead}
          />
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
